import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/api';

// Async thunks
function buildLoginRequestBody(credentials) {
  const password = credentials.password;
  const body = { password };

  if (credentials.phoneNumber) {
    const digits = String(credentials.phoneNumber).replace(/\D/g, '').slice(-10);
    if (digits.length === 10) {
      body.phoneNumber = digits;
      return body;
    }
  }

  if (credentials.email != null && String(credentials.email).trim() !== '') {
    const raw = String(credentials.email).trim();
    if (!raw.includes('@')) {
      const digits = raw.replace(/\D/g, '');
      if (digits.length === 10) {
        body.phoneNumber = digits;
        return body;
      }
    }
    body.email = raw;
    return body;
  }

  return null;
}

export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (credentials, { rejectWithValue }) => {
    try {
      const body = buildLoginRequestBody(credentials);
      if (!body || (!body.email && !body.phoneNumber)) {
        return rejectWithValue('Email or phone number is required');
      }
      const response = await axios.post(API_ENDPOINTS.AUTH_LOGIN, body);
      const { user, token, restaurant } = response.data;
      
      // Store in localStorage
      localStorage.setItem('authToken', token);
      localStorage.setItem('userData', JSON.stringify(user));
      
      // For staff, ensure restaurant data is stored
      if (restaurant) {
        localStorage.setItem('restaurantData', JSON.stringify(restaurant));
      } else if (user.role === 'staff' && user.restaurantId) {
        // If restaurant data not provided but staff has restaurantId, store it
        localStorage.setItem('restaurantId', user.restaurantId);
      }
      
      return { user, token, restaurant };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Login failed');
    }
  }
);

export const registerUser = createAsyncThunk(
  'auth/registerUser',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_ENDPOINTS.AUTH_REGISTER, userData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Registration failed');
    }
  }
);

export const registerCustomer = createAsyncThunk(
  'auth/registerCustomer',
  async (customerData, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_ENDPOINTS.AUTH_REGISTER_CUSTOMER, customerData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Customer registration failed');
    }
  }
);

export const verifyToken = createAsyncThunk(
  'auth/verifyToken',
  async (token, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_ENDPOINTS.AUTH_VERIFY_TOKEN, { token });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Token verification failed');
    }
  }
);

export const checkAuthOnLoad = createAsyncThunk(
  'auth/checkAuthOnLoad',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      const userData = localStorage.getItem('userData');
      const restaurantData = localStorage.getItem('restaurantData');

      if (token && userData) {
        const user = JSON.parse(userData);
        const restaurant = restaurantData ? JSON.parse(restaurantData) : null;
        return { user, token, restaurant };
      }
      throw new Error('No stored auth data');
    } catch (error) {
      return rejectWithValue('No valid authentication found');
    }
  }
);

const initialState = {
  user: null,
  token: null,
  restaurant: null,
  isAuthenticated: false,
  loading: true,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.restaurant = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
      
      // Clear localStorage
      localStorage.removeItem('authToken');
      localStorage.removeItem('userData');
      localStorage.removeItem('restaurantData');
    },
    clearError: (state) => {
      state.error = null;
    },
    updateRestaurantDetails: (state, action) => {
      state.restaurant = action.payload;
      if (action.payload) {
        localStorage.setItem('restaurantData', JSON.stringify(action.payload));
      }
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.restaurant = action.payload.restaurant;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Register
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Register Customer
      .addCase(registerCustomer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerCustomer.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(registerCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Verify Token
      .addCase(verifyToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyToken.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(verifyToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Check Auth on Load
      .addCase(checkAuthOnLoad.pending, (state) => {
        state.loading = true;
      })
      .addCase(checkAuthOnLoad.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.restaurant = action.payload.restaurant;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(checkAuthOnLoad.rejected, (state) => {
        state.loading = false;
        state.user = null;
        state.token = null;
        state.restaurant = null;
        state.isAuthenticated = false;
      });
  },
});

export const { logout, clearError, updateRestaurantDetails, setLoading } = authSlice.actions;
export default authSlice.reducer;

